import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';

import { ClickAwayListener, TableRow } from '@material-ui/core';
import { openObjectivePromptModal, closeObjectivePromptModal } from 'actions/objectivePromptModalActions';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';
import {
  NewStyledIconButton,
  PaddingCSS,
  StyledBox,
  StyledTooltip,
  StyledIconButton,
  wordBreakCSS,
} from 'styles/common';

import { OBJECTIVE_LEVEL_TAGS } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { getColorFromKey, haveSamePropValues } from 'constants/helper';
import { CaretDownIcon, CheckIcon2, CloseIcon2 } from 'constants/icons';
import theme from 'constants/theme';

import { showErrorSnackbar } from 'services/snackbar';

import { Typography4 } from 'uiToolkit/Typography';

import EmptyBlock from './EmptyBlock';
import ExpandedTableWithinCard from './ExpandedTableInCard';
import MenuOptions from './MenuOptions';
import { CloneObjectiveModalView, DeleteObjectiveModalGoalsRevamp } from './ModalPromptView';
import ObjectiveLevelSelectionComp from './ObjectiveLevelSelectionComp';
import ObjectiveNameAutoComplete from './ObjectiveNameAutoComplete';
import { DriverOption, DriverSingleValue } from './ReactSelectCompOverrides';
import { TagDescription } from './TagDescription';
import TagIcon from './TagIcon';
import { isPromptModalVisible } from './helpers';
import { StyledTableCell, StyledTextField, StyledSelectDropdown } from './styles';

// helpers for this component only.
// keeping them here makes more sense than moving them up in helpers.
const MAX_EXPANSION_LEVEL = 40;

export const paddingFactor = 24;
export const iconSize = 24;
export const horizontalLineWidth = paddingFactor + iconSize / 2;

export const getLeftPadding = (nestingLevel) => {
  if (nestingLevel === 0) {
    return `${paddingFactor}px`;
  }
  if (nestingLevel === 1) {
    return `${paddingFactor + paddingFactor + iconSize}px`;
  }
  if (nestingLevel > 1) {
    return `${(nestingLevel * 2 - 1) * paddingFactor + paddingFactor + iconSize}px`;
  }
  return `${paddingFactor}px`;
};

export const getLeftPosition = (nestingLevel) => {
  switch (nestingLevel) {
    case 0:
      return `${paddingFactor}px`;
    case 1:
      return `${paddingFactor + iconSize / 2}px`;
    case 2:
      return `${2 * paddingFactor + paddingFactor + iconSize / 2}px`;
    case 3:
      return `${4 * paddingFactor + paddingFactor + iconSize / 2}px`;
    case 4:
      return `${6 * paddingFactor + paddingFactor + iconSize / 2}px`;
    default:
      return `${paddingFactor}px`;
  }
};

export const StyledVerticalLine = styled.span`
  position: absolute;
  top: -35px;
  bottom: ${(props) => (props.bottom ? props.bottom : '50%')};
  left: ${(props) => (props.left ? props.left : 0)};
  display: inline-block;
  width: 1px;
  background-color: ${getColorFromKey('primary')};
`;

export const StyledHorizontalLine = styled.span`
  position: absolute;
  left: ${(props) => `-${horizontalLineWidth}px`};
  top: 50%;
  display: inline-block;
  height: 1px;
  width: ${(props) => props.width};
  background-color: ${getColorFromKey('primary')};
`;

const getVerticalLines = (nestingLevel, isCreateNewRow = false, levelsForWhichLinesToBeDrawn = []) => {
  if (nestingLevel === 0) return null;

  return (
    <>
      {levelsForWhichLinesToBeDrawn.map((num, index) => (
        <StyledVerticalLine key={`${nestingLevel}+${num}`} left={`-${getLeftPosition(nestingLevel + 1 - num)}`} />
      ))}
      <StyledHorizontalLine width={`${horizontalLineWidth}px`} />
      <StyledVerticalLine
        left={`-${horizontalLineWidth}px`}
        // bottom={isCreateNewRow ? '50%' : '-1px'}
      />
    </>
  );
};

const showExpansionIcon = (nestingLevel) => nestingLevel < 40;

const ExpansionControl = ({
  nestingLevel,
  handleExpansionClick, // pass functions using callback hook to save renders.
  // handleAddChild,
  hasChildTags,
  // canHaveChildTags,
  isCreateNewRow,
  isExpanded,
  levelsForWhichLinesToBeDrawn,
  isLastRow,
}) => (
  // return useMemo( () =>
  <>
    {getVerticalLines(nestingLevel, isCreateNewRow, levelsForWhichLinesToBeDrawn)}
    <StyledExpansionContainer>
      {showExpansionIcon(nestingLevel) && (
        <>
          {hasChildTags && (
            <NewStyledIconButton
              rightGutter={1}
              padding="none"
              height="max-content"
              onClick={handleExpansionClick}
              color="primary"
              style={{ zIndex: 5 }}
            >
              <PlayIconContainer>
                <PlayIcon rotate={isExpanded} />
              </PlayIconContainer>
            </NewStyledIconButton>
          )}
          {/* {
                canHaveChildTags && 
                  <NewStyledIconButton height='max-content' onClick={handleAddChild} color="textSecondary">
                    <StyledIcon size="header">
                      <AddCircleOutlineRoundedIcon />
                    </StyledIcon>
                  </NewStyledIconButton>
              } */}
        </>
      )}
    </StyledExpansionContainer>
  </>
);

// ,[nestingLevel, handleExpansionClick ]);
export const HoverOptionItems = styled.div`
  visibility: hidden;
`;

export const ModalCheckIcon = styled.div``;

const TruncatedContainer = styled.div`
  width: 100%;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  margin-left: 12px;
`;

const WorkBreakTypography4 = styled(Typography4)`
  ${wordBreakCSS}
`;

const SearchHighlightView = ({ value = '', searchString = '' }) => {
  const matches = match(value, searchString);
  const parts = parse(value, matches);

  return (
    <StyledTooltip title={value}>
      <TruncatedContainer>
        {parts.map((part, index) => (
          <WorkBreakTypography4
            display="inline"
            component="span"
            customColor={part.highlight ? theme.palette.text.label : theme.palette.text.heading}
          >
            {part.text}
          </WorkBreakTypography4>
        ))}
      </TruncatedContainer>
    </StyledTooltip>
  );
};

const HashTagComp = ({
  name,
  handleKeyDown,
  handleTagChange,
  // validationError,
  isCreateNewRow,
  children,
  nestingLevel,
  isEditing,
  isRenaming,
  isExpandable,
  subTypeIcon = null,
  getFocus,
  selectedTimeBoundPeriod,
  searchString = '',
  modalView = false,
}) => (
  // return useMemo( () =>
  <StyledTableCell paddingtop="0" paddingBottom="0" paddingLeft={getLeftPadding(nestingLevel)} display="flex" border>
    <FlexContainer paddingLeft="0">
      {children}
      <TextFieldContainer isRenaming={isRenaming} isEditing={isEditing}>
        {!isEditing && subTypeIcon}

        {isEditing && (
          <ObjectiveNameAutoComplete
            disabled={!isEditing}
            onInputChange={handleTagChange}
            inputValue={name}
            selectedTimeBoundPeriod={selectedTimeBoundPeriod}
            autoFocus={isCreateNewRow && !modalView}
            // searchString={searchString}
          />
        )}

        {!isEditing && isRenaming && (
          <StyledTextField
            disabled={!isRenaming}
            color={theme.palette.grey.dark}
            placeholder="Name"
            name="name"
            fullWidth
            margin="none"
            variant="outlined"
            value={name}
            onKeyDown={handleKeyDown}
            onChange={(e) => handleTagChange(e.target.value)}
            style={{ height: '40px', justifyContent: 'center' }}
          />
        )}

        {!isEditing && !isRenaming && <SearchHighlightView value={name} searchString={searchString} />}
      </TextFieldContainer>
      {/* {validationError && (
          <ErrorSubText center>Special characters and leading spaces are not allowed</ErrorSubText>
        )} */}
    </FlexContainer>
  </StyledTableCell>
);
// ,[validationError, handleKeyDown, handleTagChange, name])
// }, [description, handleKeyDown, handleOnChange ])

const NewEditableTagRow = ({
  tag,
  onUpdate,
  nestingLevel = 0,
  isExpandable,
  isEditing,
  isRenaming,
  toggleEditing,
  toggleRenaming,
  // being not active is considered as deleted.
  // onActiveToggle,
  hasChildTags = true,
  canHaveChildTags = true,
  enableExpansion: enableParentExpansion = () => {},
  disableExpansion: disableParentExpansion = () => {},

  // modalView
  modalView = true,
  getFocus = false,
  enableCreation = true,
  selectTag,
  levelsForWhichLinesToBeDrawn,
  isLastRow,
  editingTags = [],
  renamingTags = [],
  setEditingTags = () => {},
  setRenamingTags = () => {},
  timePeriodData = {},
  timePeriodsList = [],
  refreshCurrentPage,
  selectedTimeBoundPeriod,
  searchString,
  selectedObjective = null,
  selectedObjectiveHierarchy = [],
  isScrolledToSelected = false,
  setIsScrolledToSelected = () => {},
}) => {
  const isCreateNewRow = !canHaveChildTags;
  const [currentTag, setCurrentTag] = useState(tag);
  // const [validationError, setValidationError] = useState(false);

  const [showExpandedCard, setShowExpandedCard] = useState(!!selectedObjectiveHierarchy?.includes(tag.objectiveId));

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const [showChildTagCreationRow, setShowChildTagCreationRow] = useState(false);
  const isSelected = selectedObjective?.objectiveId === tag.objectiveId;

  const objectiveRowRef = useRef(null);

  const dispatch = useDispatch();

  const handleExpansionClick = (event) => {
    event.stopPropagation();
    setShowExpandedCard((prev) => !prev);
  };

  const handleAddClick = (event) => {
    event.stopPropagation();
    setShowChildTagCreationRow(true);
  };

  const { name, description, objectiveLevel } = currentTag;

  const updateTag = (currentTag) => {
    onUpdate({ ...currentTag }, false, isRenaming);
  };

  const handleOnChange = useCallback(({ target: { name, value } }) => {
    setCurrentTag((oldTag) => ({ ...oldTag, [name]: value }));
  }, []);

  const handleTagSubTypeChange = useCallback(
    (tagData) => {
      setCurrentTag((oldTag) => ({ ...oldTag, objectiveLevel: tagData.value }));
    },
    [setCurrentTag]
  );

  const handleTagChange = useCallback((value, objectiveData = null, isNew = false) => {
    if (isNew) {
      setCurrentTag((oldTag) => ({
        ...oldTag,
        name: value,
        isNew: true,
      }));
    } else {
      setCurrentTag((oldTag) => ({
        ...oldTag,
        name: value,
        ...(objectiveData ? { objectiveId: objectiveData.objectiveId } : {}),
      }));
    }
  }, []);

  const cancelEditing = () => {
    setCurrentTag(tag);
    if (tag.isEditing) toggleEditing(tag.objectiveId);
  };

  const cancelRenaming = () => {
    setCurrentTag(tag);
    if (tag.isRenaming) toggleRenaming(tag.objectiveId);
  };

  // listen to changes in parent tags array.
  useEffect(() => {
    setCurrentTag(tag);
  }, [tag.active, tag.name, tag.description, tag.objectiveId, isEditing, isRenaming]);

  // autoscroll selected objective
  useEffect(() => {
    if (isSelected && selectedObjectiveHierarchy.length === nestingLevel && !isScrolledToSelected) {
      const selectedRow = objectiveRowRef.current;
      selectedRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setIsScrolledToSelected(true);
    }
  }, []);

  const enableEditing = useCallback(() => {
    if (!isEditing) toggleEditing(tag.objectiveId);
  }, [tag.objectiveId, isEditing]);

  const enableRenaming = useCallback(() => {
    if (!isRenaming) toggleRenaming(tag.objectiveId);
  }, [tag.objectiveId, isRenaming]);

  const handleClickAway = useCallback(() => {
    if (isPromptModalVisible()) return;
    if (isEditing) {
      const propertyList = ['name', 'displayName', 'description', 'objectiveLevel'];
      if (haveSamePropValues(tag, currentTag, propertyList)) cancelEditing();
      else if (!name) {
        dispatch(showErrorSnackbar('Please enter objective name!'));
      } else if (!objectiveLevel) {
        dispatch(showErrorSnackbar('Please select objective level!'));
      } else {
        updateTag(currentTag);
      }
    }
    if (isRenaming) {
      const propertyList = ['name', 'displayName', 'description', 'objectiveLevel'];
      if (haveSamePropValues(tag, currentTag, propertyList)) cancelRenaming();
      else if (!name) {
        dispatch(showErrorSnackbar('Please enter objective name!'));
      } else if (!objectiveLevel) {
        dispatch(showErrorSnackbar('Please select objective level!'));
      } else {
        updateTag(currentTag);
      }
    }
  }, [currentTag, isEditing, updateTag, isRenaming]);

  const subTypeIcon = useMemo(
    () => (
      <div style={{ margin: '0 12px 0 16px', display: 'flex', alignItems: 'center' }}>
        <TagIcon tagId={objectiveLevel || undefined} />
      </div>
    ),
    [objectiveLevel]
  );

  const handleSaveTag = useCallback(() => {
    const tagRowPropertyList = ['name', 'displayName', 'description', 'objectiveLevel'];
    if (isEditing) {
      if (haveSamePropValues(tag, currentTag, tagRowPropertyList)) cancelEditing();
      else if (!name) {
        dispatch(showErrorSnackbar('Please enter objective name!'));
      } else if (!objectiveLevel) {
        dispatch(showErrorSnackbar('Please select objective level!'));
      } else {
        updateTag(currentTag);
      }
    }
    if (isRenaming) {
      if (haveSamePropValues(tag, currentTag, tagRowPropertyList)) cancelRenaming();
      else if (!name) {
        dispatch(showErrorSnackbar('Please enter objective name!'));
      } else if (!objectiveLevel) {
        dispatch(showErrorSnackbar('Please select objective level!'));
      } else {
        updateTag(currentTag);
      }
    }
  }, [currentTag, isEditing, updateTag, isRenaming]);

  const handleKeyDown = ({ keyCode, key, target }) => {
    if (keyCode === 27) {
      // this will work since everytime update is passed up to parent.
      target.blur();
      cancelEditing();
      cancelRenaming();
    }
    if (key === 'Enter') {
      if (isPromptModalVisible()) return;

      handleSaveTag();
    }
  };

  const getModalObjectiveLevelColumn = () => (
    <StyledTableCell paddingtop="0" paddingBottom="0" paddingLeft="0">
      <ObjectiveLevelSelectionComp
        objectiveLevel={objectiveLevel}
        onSelect={handleTagSubTypeChange}
        isEditing={isEditing}
        isRenaming={isRenaming}
        options={OBJECTIVE_LEVEL_TAGS}
      />
    </StyledTableCell>
  );

  return (
    <>
      <ClickAwayListener onClickAway={handleClickAway}>
        <StyledTableRow
          isEditing={isEditing}
          isRenaming={isRenaming}
          modalView={modalView}
          isSelected={isSelected}
          ref={objectiveRowRef}
          onClick={(event) => {
            if (isEditing) return;
            if (isRenaming) return;

            if (modalView) {
              if (editingTags.length > 0) {
                event.stopPropagation();
                return;
              }
              handleExpansionClick(event);
              // selectTag(tag);
            } else {
              enableRenaming();
            }
          }}
        >
          <HashTagComp
            isEditing={isEditing}
            isRenaming={isRenaming}
            name={name}
            handleTagChange={handleTagChange}
            handleKeyDown={handleKeyDown}
            nestingLevel={nestingLevel}
            isCreateNewRow={isCreateNewRow}
            isExpandable={isExpandable}
            subTypeIcon={modalView ? subTypeIcon : null}
            getFocus={getFocus}
            selectedTimeBoundPeriod={selectedTimeBoundPeriod}
            searchString={searchString}
            modalView={modalView}
          >
            {isExpandable && (
              <ExpansionControl
                nestingLevel={nestingLevel}
                handleExpansionClick={handleExpansionClick}
                handleAddChild={handleAddClick}
                hasChildTags={hasChildTags}
                isCreateNewRow={enableCreation ? isCreateNewRow : isLastRow}
                canHaveChildTags={false}
                isExpanded={showExpandedCard}
                levelsForWhichLinesToBeDrawn={levelsForWhichLinesToBeDrawn}
                isLastRow={isLastRow}
              />
            )}
            {modalView && isEditing && getModalObjectiveLevelColumn()}
          </HashTagComp>
          {!modalView && (
            <StyledTableCell paddingtop="0" paddingBottom="0" border>
              {/* <ObjectiveLevelSelectionComp
                selectedTag={objectiveLevel}
                onSelect={handleTagSubTypeChange}
                isEditing={isEditing}
              /> */}
              {!isEditing && !OBJECTIVE_LEVEL_TAGS.filter((opt) => opt.value === objectiveLevel)[0]?.value ? (
                <EmptyBlock />
              ) : (
                <StyledSelectDropdown
                  isEditing={isEditing}
                  isRenaming={isRenaming}
                  options={OBJECTIVE_LEVEL_TAGS}
                  onChange={handleTagSubTypeChange}
                  value={OBJECTIVE_LEVEL_TAGS.filter((opt) => opt.value === objectiveLevel)[0]}
                  classNamePrefix="driver-select"
                  placeholder="Select Type"
                  components={{
                    Option: DriverOption,
                    SingleValue: DriverSingleValue,
                  }}
                />
              )}
            </StyledTableCell>
          )}

          <TagDescription
            isEditing={isEditing}
            isRenaming={isRenaming}
            description={description}
            handleKeyDown={handleKeyDown}
            handleOnChange={handleOnChange}
          />
          <StyledTableCell textAlign="right" paddingTop={!isEditing && '0'} paddingBottom={!isEditing && '0'} border>
            <FlexContainer style={{ justifyContent: 'flex-end' }}>
              {(isEditing || isRenaming) && (
                <FullWidthBox flexcontainer justifycontent="flex-end" alignitems="center">
                  <StyledIconButton rightGutter={2} onClick={handleSaveTag}>
                    <CheckIcon2
                      circularOutline
                      containerSize="fixed24px"
                      size="fixed10px"
                      color="green"
                      bgColor="white"
                    />
                  </StyledIconButton>
                  <StyledIconButton
                    onClick={() => {
                      if (isEditing) {
                        cancelEditing();
                      }
                      if (isRenaming) {
                        cancelRenaming();
                      }
                    }}
                  >
                    <CloseIcon2
                      circularOutline
                      containerSize="fixed24px"
                      size="fixed10px"
                      color="red"
                      bgColor="white"
                    />
                  </StyledIconButton>
                </FullWidthBox>
              )}

              {!isEditing && !isRenaming && enableCreation && (
                <HoverOptionItems>
                  <MenuOptions
                    cloneTag={(event) => {
                      enableEditing();
                      const promptHandler = () => {
                        cancelEditing();
                        cancelRenaming();
                        dispatch(closeObjectivePromptModal());
                      };
                      dispatch(
                        openObjectivePromptModal({
                          contentComponent: (
                            <CloneObjectiveModalView
                              onCompletion={promptHandler}
                              sourceObjective={tag}
                              timePeriodData={timePeriodData}
                              timePeriodsList={timePeriodsList}
                            />
                          ),
                        })
                      );
                    }}
                    editTag={(event) => {
                      if (isEditing) return;

                      enableEditing();
                    }}
                    renameTag={(event) => {
                      if (isRenaming) return;

                      enableRenaming();
                    }}
                    deleteTag={(event) => {
                      enableEditing();
                      const promptHandler = (deletionSuccess) => {
                        if (deletionSuccess) {
                          refreshCurrentPage();
                        }
                        cancelEditing();
                        cancelRenaming();
                        dispatch(closeObjectivePromptModal());
                      };
                      dispatch(
                        openObjectivePromptModal({
                          contentComponent: (
                            <DeleteObjectiveModalGoalsRevamp
                              onCompletion={promptHandler}
                              objectiveTimeBoundId={tag.objectiveTimeBoundId}
                              tag={tag}
                            />
                          ),
                        })
                      );
                    }}
                  />
                </HoverOptionItems>
              )}
              {modalView && !isEditing && !isRenaming && (
                <ModalCheckIcon>
                  <CheckIconButton leftGutter={2} onClick={() => selectTag(tag)}>
                    <StyledTooltip title={`Select this ${modifyString(objectivesName, StringModifier.SENTENCE_CASE)}`}>
                      <CheckIcon2
                        circularOutline
                        containerSize="fixed24px"
                        size="fixed10px"
                        color="grey"
                        // bgColor="white"
                      />
                    </StyledTooltip>
                  </CheckIconButton>
                </ModalCheckIcon>
              )}
            </FlexContainer>
          </StyledTableCell>
        </StyledTableRow>
        {/* </> */}
      </ClickAwayListener>
      {(showExpandedCard || showChildTagCreationRow) && (
        <ExpandedTableWithinCard
          tableTag={tag}
          nestingLevel={nestingLevel + 1}
          showExpandedCard={showExpandedCard}
          enableParentExpansion={enableParentExpansion}
          disableParentExpansion={disableParentExpansion}
          modalView={modalView}
          enableCreation={nestingLevel + 1 < 5 && enableCreation}
          selectTag={selectTag}
          levelsForWhichLinesToBeDrawn={levelsForWhichLinesToBeDrawn}
          editingTags={editingTags}
          renamingTags={renamingTags}
          setEditingTags={setEditingTags}
          setRenamingTags={setRenamingTags}
          timePeriodData={timePeriodData}
          timePeriodsList={timePeriodsList}
          selectedTimeBoundPeriod={selectedTimeBoundPeriod}
          searchString={searchString}
          selectedObjective={selectedObjective}
          selectedObjectiveHierarchy={selectedObjectiveHierarchy}
          isScrolledToSelected={isScrolledToSelected}
          setIsScrolledToSelected={setIsScrolledToSelected}
        />
      )}
    </>
  );
};

const CheckIconButton = styled(StyledIconButton)`
  background: white;
`;

const ErrorSubText = styled.span`
  font-size: 12px;
  margin: -5px 0 0 0;
  display: inline-block;
  color: red;
  opacity: 0.75;
  line-height: 1rem;
`;

const StyledExpansionContainer = styled.div`
  display: flex;
  align-items: center;
  ${PaddingCSS}
`;

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  ${PaddingCSS};
`;

const StyledTableRow = styled(TableRow)`
  min-height: 62px;
  ${(props) =>
    props.modalView &&
    !props.isEditing &&
    !props.isRenaming &&
    css`
      td * {
        cursor: pointer;
      }
    `};

  ${(props) =>
    (props.isEditing || props.isRenaming) &&
    css`
      background-color: #e9f6ff;
    `}

  ${(props) =>
    props.isSelected &&
    css`
      background-color: #e9f6ff;
      ${ModalCheckIcon} {
        svg {
          fill: ${(props) => props.theme.palette.primary.main};
        }
        button span span {
          border-color: ${(props) => props.theme.palette.primary.main};
        }
      }
    `}

  &:hover {
    background-color: #e9f6ff;
    ${ModalCheckIcon} {
      svg {
        fill: ${(props) => props.theme.palette.primary.main};
      }
      button span span {
        border-color: ${(props) => props.theme.palette.primary.main};
      }
    }

    ${HoverOptionItems} {
      visibility: visible;
      button {
        cursor: pointer;
      }
    }
  }
`;

const TextFieldContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-height: 40px;
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
  padding-left: 0px;


  
  ${(props) =>
    (props.isEditing || props.isRenaming) &&
    css`
      background-color: ${props.theme.palette.background.paper};
      border: 1px solid ${props.theme.palette.borders.lighterGrey};
      border-radius: 5px;
    `}
    input {
      padding-left 12px;
    }
`;

const PlayIconContainer = styled.div`
  background-color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 24px !important;
  width: 24px !important;
  border: ${(props) => `1px solid ${theme.palette.primary.main}`};
  border-radius: 50%;
  border-color: ${(props) => theme.palette.primary.main};
`;

const PlayIcon = styled(CaretDownIcon)`
  svg {
    width: 10px;
    height: 10px;
    margin-top: 2px;
    margin-left: 2px;
    fill: ${(props) => props.theme.palette.primary.main};
    ${({ rotate }) =>
      rotate &&
      `transform: rotate(90deg);
        margin-left: -2px;
        margin-top: 3px;
    `};
  }
`;

const FullWidthBox = styled(StyledBox)`
  width: 100%;
`;

const EmptyBar = styled.div`
  width: 2.25rem;
  height: 0.375rem;
  border-radius: 0.5rem;
  margin-bottom: ${(props) => props.selected && '0.375rem'};
  background-color: ${(props) => props.theme.palette.icons.grey3};
`;

export default NewEditableTagRow;
